import { useCallback, useEffect, useRef, useState } from 'react';
import { PackageOpen, Upload, X } from 'lucide-react';

import { attachTaskFile, runInstallTask } from '@/lib/installRunner';
import { useDevice } from '@/store/device';
import { useDeviceDisplayName } from '@/lib/displayName';
import { useInstallTasks } from '@/store/installTasks';
import { useMirror } from '@/store/mirror';

/**
 * Global drag-and-drop install zone.
 *
 * Behaviour (per the user's UX feedback):
 *   - The fullscreen overlay ONLY shows while the user is dragging
 *     over the window (dragenter…dragleave). The moment they release
 *     the file the overlay disappears — installation never blocks
 *     the rest of the SPA again.
 *   - On drop we hand off to `InstallTasksDock` via the global
 *     `useInstallTasks` store: enqueue a task, attach the File for
 *     later retries, kick off `runInstallTask` and return.
 *   - Multiple files in one drop fan out into one task per file.
 *
 * Mounts once at app root. Listens with `capture: true` so it runs
 * before FloatingMirror's per-window drop handler.
 */

interface DropState {
  active: boolean;
  depth: number;
}

interface QuickToast {
  id: number;
  level: 'info' | 'error';
  text: string;
}

function fileExt(name: string): 'apk' | 'xapk' | null {
  if (/\.xapk$/i.test(name)) return 'xapk';
  if (/\.apk$/i.test(name)) return 'apk';
  return null;
}

function hasFile(dt: DataTransfer | null): boolean {
  if (!dt) return false;
  return Array.from(dt.types).includes('Files');
}

export function InstallDropZone(): JSX.Element | null {
  const mirrorSerial = useMirror((s) => (s.isOpen ? s.serial : null));
  const focusSerial = useDevice((s) => s.serial);
  const serial = mirrorSerial ?? focusSerial ?? null;
  const displayName = useDeviceDisplayName(serial);

  const [drop, setDrop] = useState<DropState>({ active: false, depth: 0 });
  const [toasts, setToasts] = useState<QuickToast[]>([]);
  const depthRef = useRef(0);

  const enqueue = useInstallTasks((s) => s.enqueue);
  const setExpanded = useInstallTasks((s) => s.setExpanded);

  const pushToast = useCallback((level: QuickToast['level'], text: string) => {
    const id = Date.now() + Math.random();
    setToasts((cur) => [...cur, { id, level, text }]);
    window.setTimeout(() => {
      setToasts((cur) => cur.filter((t) => t.id !== id));
    }, 4_000);
  }, []);

  const resetOverlay = useCallback(() => {
    depthRef.current = 0;
    setDrop({ active: false, depth: 0 });
  }, []);

  const startInstall = useCallback(
    (file: File, targetSerial: string, label: string | null) => {
      const ext = fileExt(file.name);
      if (!ext) return;
      const taskId = enqueue({
        fileName: file.name,
        ext,
        serial: targetSerial,
        serialLabel: label ?? undefined,
        statusText: '排队中',
        totalBytes: file.size,
      });
      attachTaskFile(taskId, file);
      // Expand the dock so the user immediately sees the new task.
      setExpanded(true);
      // Fire-and-forget — runInstallTask updates the store as it
      // progresses and never rejects.
      void runInstallTask(taskId, { file, reinstall: true });
    },
    [enqueue, setExpanded]
  );

  useEffect(() => {
    const onEnter = (e: DragEvent): void => {
      if (!hasFile(e.dataTransfer)) return;
      e.preventDefault();
      depthRef.current += 1;
      setDrop({ active: true, depth: depthRef.current });
    };
    const onOver = (e: DragEvent): void => {
      if (!hasFile(e.dataTransfer)) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = serial ? 'copy' : 'none';
    };
    const onLeave = (e: DragEvent): void => {
      if (!hasFile(e.dataTransfer)) return;
      depthRef.current = Math.max(0, depthRef.current - 1);
      if (depthRef.current === 0) setDrop({ active: false, depth: 0 });
    };
    const onDrop = (e: DragEvent): void => {
      if (!hasFile(e.dataTransfer)) return;
      const files = Array.from(e.dataTransfer?.files ?? []);
      const interesting = files.filter((f) => fileExt(f.name) !== null);
      if (interesting.length === 0) {
        // Not our concern — let FloatingMirror or other regions handle.
        resetOverlay();
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      // Always release the overlay first — the user must not see a
      // fullscreen "请稍候" panel after release.
      resetOverlay();

      if (!serial) {
        pushToast('error', '请先选定一台设备再拖入 apk / xapk');
        return;
      }

      const label = displayName && displayName !== serial ? `${displayName}` : serial;
      for (const file of interesting) {
        startInstall(file, serial, label);
      }
      if (files.length !== interesting.length) {
        pushToast('info', `${files.length - interesting.length} 个非 apk/xapk 文件已忽略`);
      }
    };

    window.addEventListener('dragenter', onEnter, { capture: true });
    window.addEventListener('dragover', onOver, { capture: true });
    window.addEventListener('dragleave', onLeave, { capture: true });
    window.addEventListener('drop', onDrop, { capture: true });
    return () => {
      window.removeEventListener('dragenter', onEnter, { capture: true });
      window.removeEventListener('dragover', onOver, { capture: true });
      window.removeEventListener('dragleave', onLeave, { capture: true });
      window.removeEventListener('drop', onDrop, { capture: true });
    };
  }, [serial, displayName, resetOverlay, pushToast, startInstall]);

  return (
    <>
      {drop.active && (
        <div className="pointer-events-none fixed inset-0 z-[60] flex items-center justify-center bg-black/30 backdrop-blur-[2px]">
          <div className="card-pad pointer-events-auto flex max-w-md flex-col items-center gap-2 border-2 border-dashed border-accent text-center">
            {serial ? (
              <PackageOpen className="h-7 w-7 text-accent" />
            ) : (
              <Upload className="h-7 w-7 text-rose-500" />
            )}
            <div className="text-sm font-medium text-ink">
              {serial
                ? `释放以安装到 ${displayName || serial}`
                : '请先选定一台设备再拖入'}
            </div>
            <div className="text-[11px] text-ink-dim">支持 .apk / .xapk（含 splits + OBB）</div>
            <div className="text-[10.5px] text-ink-dim">
              安装将在右下角后台进行，不会阻塞当前界面
            </div>
          </div>
        </div>
      )}

      {toasts.length > 0 && (
        <div className="pointer-events-none fixed bottom-4 right-[24rem] z-50 flex flex-col gap-2">
          {toasts.map((t) => (
            <div
              key={t.id}
              className={`pointer-events-auto flex items-center gap-2 rounded-md border px-3 py-2 text-[12px] shadow-card ${
                t.level === 'error'
                  ? 'border-rose-500/50 bg-rose-500/10 text-rose-700'
                  : 'border-line bg-bg-card text-ink'
              }`}
            >
              <span className="max-w-[280px] truncate">{t.text}</span>
              <button
                type="button"
                className="ml-2 text-ink-dim hover:text-ink"
                onClick={() => setToasts((cur) => cur.filter((x) => x.id !== t.id))}
                aria-label="关闭"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
